import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { Link, useNavigate } from 'react-router';

import { CustomUserDTO } from '@/types/api-types';

import IconChevronLeft from '@/assets/svg/icon-chevron-left.svg?react';
import IconUser from '@/assets/svg/icon-user.svg?react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useGlobalStore } from '@/stores/globalStore';

import { INSURANCE_AREA, USER_ROLES } from '@/constants/global-constants';
import { adminRoutes } from '@/constants/route-settings';

interface AddUserForm extends Omit<CustomUserDTO, 'uuid' | 'directories'> {
  directories: string;
}

const cleanPhoneNumber = (phone: string) => phone.replace(/[^\d+]/g, '');

export function AddUserPage() {
  const navigate = useNavigate();
  const { getDirectories, directories } = useApiManagerStore();
  const { createUserAdmin } = useAuthStore();
  const { createUser } = useGlobalStore();

  const form = useForm<AddUserForm>({
    defaultValues: {
      last_name: '',
      first_name: '',
      phone_number: '',
      email: '',
    },
  });

  const onSubmit = async (data: AddUserForm) => {
    const formData = {
      ...data,
      phone_number: cleanPhoneNumber(data.phone_number),
    };
    try {
      const user = await createUserAdmin({
        ...formData,
        email: data.email.toLowerCase(),
      });

      if (user === null) {
        form.setError('email', {
          message: 'Користувач з таким email вже існує',
        });
        return;
      }

      await createUser({
        ...formData,
        user_type: data.user_type,
        email: data.email.toLowerCase(),
        directory_set:
          data.user_type === USER_ROLES.EXECUTOR.value
            ? [data.directories]
            : directories.map((directory) => directory.uuid),
        is_active: true,
        uuid: user.uuid,
        static_token: user.staticToken,
      });

      navigate(adminRoutes.users);
    } catch (error) {}
  };

  useEffect(() => {
    getDirectories();
  }, []);

  const userType = form.watch('user_type');

  useEffect(() => {
    if (
      form.getFieldState('user_type').isDirty &&
      userType !== USER_ROLES.EXECUTOR.value
    ) {
      form.setValue('directories', 'Головний офіс', {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (form.getFieldState('user_type').isDirty) {
      form.resetField('directories');
    }
  }, [userType, form]);

  return (
    <>
      <div className="sticky top-0 flex w-full flex-col shadow-sm sm:static">
        <nav className="flex h-[50px] items-center border-b bg-white pl-5 sm:h-[70px] sm:pl-[50px]">
          <Link to={adminRoutes.users} className="flex items-center gap-2.5">
            <IconChevronLeft className="h-[14px] w-[14px] fill-blue-300" />
            <div className="flex gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded bg-blue-300">
                <IconUser className="h-9 w-9" />
              </div>
            </div>
          </Link>
        </nav>
      </div>
      <div className="flex w-full flex-wrap justify-start gap-6 p-5 sm:p-12">
        <Card className="max-w-[920px] flex-1 rounded-md p-7 shadow-sm">
          <CardContent className="p-0">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="flex grid-cols-2 flex-col sm:grid">
                  {/* Basic Data Section */}
                  <div className="col-span-1 row-span-2 space-y-6 border-b border-r-0 pb-7 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-7">
                    <div>
                      <h2 className="mb-2 text-2xl font-semibold">
                        Основні дані
                      </h2>
                      <p className="mb-6 text-primary">
                        Блок основних даних користувача системи
                      </p>
                    </div>

                    <div className="flex flex-col gap-4">
                      <FormField
                        control={form.control}
                        name="last_name"
                        rules={{ required: "Обов'язкове поле" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="Прізвище" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="first_name"
                        rules={{ required: "Обов'язкове поле" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="Ім'я" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="user_type"
                        rules={{ required: "Обов'язкове поле" }}
                        render={({ field }) => (
                          <FormItem>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Тип користувача" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value={USER_ROLES.EXECUTOR.value}>
                                  <span>{USER_ROLES.EXECUTOR.title}</span>
                                </SelectItem>
                                <SelectItem value={USER_ROLES.CONTROLLER.value}>
                                  <span>{USER_ROLES.CONTROLLER.title}</span>
                                </SelectItem>

                                <SelectItem
                                  value={USER_ROLES.EXTERNAL_SYSTEM.value}
                                >
                                  <span>
                                    {USER_ROLES.EXTERNAL_SYSTEM.title}
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="directories"
                        rules={{ required: "Обов'язкове поле" }}
                        render={({ field }) => (
                          <FormItem>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={userType !== USER_ROLES.EXECUTOR.value}
                            >
                              <FormControl>
                                <SelectTrigger className="[&[data-placeholder]]:text-gray-200">
                                  <SelectValue placeholder="Дирекція" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {userType !== USER_ROLES.EXECUTOR.value && (
                                  <SelectItem value="Головний офіс">
                                    Головний офіс
                                  </SelectItem>
                                )}
                                {directories.map((directory) => (
                                  <SelectItem
                                    key={directory.uuid}
                                    value={directory.uuid}
                                  >
                                    {directory.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="insurance_area"
                        rules={{ required: "Обов'язкове поле" }}
                        render={({ field }) => (
                          <FormItem>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Напрямок" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {Object.values(INSURANCE_AREA).map((area) => (
                                  <SelectItem key={area} value={area}>
                                    {area}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Contact Data Section */}
                  <div className="col-start-2 row-start-1 mb-7 mt-5 space-y-6 sm:mb-0 sm:mt-0 sm:pl-7">
                    <div>
                      <h2 className="mb-2 text-2xl font-semibold">
                        Контактні дані
                      </h2>
                      <p className="mb-6 text-primary">
                        Блок контактних даних користувача системи
                      </p>
                    </div>

                    <div className="flex flex-col gap-4">
                      <FormField
                        control={form.control}
                        name="phone_number"
                        rules={{
                          required: "Обов'язкове поле",
                          pattern: {
                            value: /^\+38\s?\(0\d{2}\)\s?\d{3}-\d{2}-\d{2}$/,
                            message: 'Невірний формат телефону',
                          },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <InputMask
                                mask="+38 (099) 999-99-99"
                                maskChar="_"
                                value={field.value || ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                onPaste={(
                                  e: React.ClipboardEvent<HTMLInputElement>,
                                ) => {
                                  // Prevent the default paste behavior
                                  e.preventDefault();

                                  // Get the pasted text
                                  const pastedText =
                                    e.clipboardData.getData('text');

                                  // Check if it's a full number starting with +380
                                  if (pastedText.startsWith('+380')) {
                                    // Remove +380 and keep only the remaining digits
                                    const remaining = pastedText.substring(4);

                                    // Format the remaining digits to fit the mask format
                                    if (remaining.length >= 9) {
                                      // Format: +38 (0xx) xxx-xx-xx
                                      const areaCode = remaining.substring(
                                        0,
                                        2,
                                      );
                                      const firstPart = remaining.substring(
                                        2,
                                        5,
                                      );
                                      const secondPart = remaining.substring(
                                        5,
                                        7,
                                      );
                                      const thirdPart = remaining.substring(
                                        7,
                                        9,
                                      );

                                      const formattedNumber = `+38 (0${areaCode}) ${firstPart}-${secondPart}-${thirdPart}`;
                                      field.onChange(formattedNumber);
                                    } else {
                                      // If format doesn't match, just set what we have
                                      field.onChange(pastedText);
                                    }
                                  } else {
                                    // For other pastes, let the input mask handle it
                                    field.onChange(pastedText);
                                  }
                                }}
                              >
                                {(inputProps) => {
                                  return (
                                    <input
                                      {...inputProps}
                                      placeholder="Телефон"
                                      type="tel"
                                      autoComplete="tel"
                                      className={cn(
                                        'peer relative z-10 flex h-[50px] w-full rounded-md border border-gray-200 bg-transparent',
                                        'px-5 py-3.5 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium',
                                        'focus-visible:outline-none',
                                        'hover:border-primary hover:shadow-[0px_0px_5px_2px_#00000040] md:px-3.5 md:py-2 md:text-sm lg:text-base',
                                        'transition-color disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:shadow-none',
                                        field.value
                                          ? 'text-primary'
                                          : 'text-gray-200',
                                      )}
                                      aria-describedby="phone-number-description"
                                    />
                                  );
                                }}
                              </InputMask>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        rules={{
                          required: "Обов'язкове поле",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Невірний формат email',
                          },
                        }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col space-y-1">
                            <FormControl>
                              <Input
                                {...field}
                                type="email"
                                placeholder="Email"
                              />
                            </FormControl>
                            <FormMessage />
                            <span className="text-center text-xs text-gray-200">
                              Заповніть{' '}
                              <span className="text-xs text-destructive">
                                усі поля
                              </span>{' '}
                              для можливості додання користувача
                            </span>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end sm:col-start-2 sm:row-start-2 sm:gap-4 sm:self-end">
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={
                        !Object.keys(form.formState.dirtyFields).length ||
                        form.formState.isSubmitting
                      }
                    >
                      {form.formState.isSubmitting ? 'Додаємо...' : 'Додати'}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
